import type { Location } from 'history';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import type { Campaign, MailrailBaseEmailCampaign } from '@feathr/blackbox';
import { useAccount } from '@feathr/extender/state';
import type { IBaseAttributes, Model } from '@feathr/rachis';

import { setRedirectWarning, unsetRedirectWarning } from '../CampaignEditPage';
import { usePendingImportsPublishGuard } from '../PendingImportsConfirmModal';
import { save } from '../SaveCampaignButton';
import type { TConfirmVariant } from './MailrailChangeConfirmModal.utils';

export interface IUseMailrailSaveGuardsParams {
  campaign: MailrailBaseEmailCampaign;
  /** Every model the save writes, the campaign's templates included. */
  childModels: Array<Model<IBaseAttributes>>;
  grandchildModels: Array<Model<IBaseAttributes>>;
  preSave: (campaign: Campaign) => Promise<void> | void;
  postSave: (campaign: Campaign, stateChanged?: boolean) => Promise<void> | void;
}

export interface IUseMailrailSaveGuards {
  confirmVariant: TConfirmVariant | null;
  closeConfirmModal: () => void;
  onPublishClick: () => void;
  onSaveClick: () => void;
  confirmLiveSave: () => Promise<void>;
  confirmRepublish: () => Promise<void>;
  discardAndContinue: () => void;
  continueAfterSave: (campaign: Campaign, stateChanged?: boolean) => Promise<void>;
  isAudienceModalOpen: boolean;
  closeAudienceModal: () => void;
  confirmAudience: () => Promise<void>;
  isPendingImportsModalOpen: boolean;
  closePendingImportsModal: () => void;
  publishAnyway: () => Promise<void>;
  /** True when the campaign or any model the save writes has unsaved changes. */
  isDirty: boolean;
  /** True while a guards-driven save or publish is running; disable the action-bar buttons on it. */
  isSaving: boolean;
}

export function useMailrailSaveGuards({
  campaign,
  childModels,
  grandchildModels,
  preSave,
  postSave,
}: IUseMailrailSaveGuardsParams): IUseMailrailSaveGuards {
  const { t } = useTranslation();
  const account = useAccount();
  const history = useHistory();

  const dirtyChildren = [...childModels, ...grandchildModels].filter((model) => model.isDirty);
  const isDirty = campaign.isDirty || !!dirtyChildren.length;

  const [confirmVariant, setConfirmVariant] = useState<TConfirmVariant | null>(null);
  const [isAudienceModalOpen, setIsAudienceModalOpen] = useState(false);
  // The publish click usually only opens a confirm modal, so the bracket lives around the actual
  // save() executions below, not the click handlers.
  const [isSaving, setIsSaving] = useState(false);
  const pendingLocationRef = useRef<Location | null>(null);
  const bypassRef = useRef(false);

  async function publish(): Promise<void> {
    setIsSaving(true);
    try {
      await save({
        campaign,
        childModels,
        grandchildModels,
        preSave,
        postSave,
        shouldChangeState: true,
        t,
        accountId: account.id,
      });
    } finally {
      setIsSaving(false);
    }
  }

  async function doSave(): Promise<void> {
    setIsSaving(true);
    try {
      await save({
        campaign,
        childModels,
        grandchildModels,
        preSave,
        postSave,
        shouldChangeState: false,
        t,
        accountId: account.id,
      });
    } finally {
      setIsSaving(false);
    }
  }

  const { isPendingImportsModalOpen, closePendingImportsModal, requestPublish, publishAnyway } =
    usePendingImportsPublishGuard(publish);

  function onPublishClick(): void {
    if (campaign.isStopped || campaign.isComplete) {
      setConfirmVariant('republish');
      return;
    }
    if (campaign.sendsBulkAtStart) {
      setIsAudienceModalOpen(true);
    } else {
      void requestPublish();
    }
  }

  function closeAudienceModal(): void {
    setIsAudienceModalOpen(false);
  }

  async function confirmAudience(): Promise<void> {
    setIsAudienceModalOpen(false);
    await requestPublish();
  }

  async function confirmRepublish(): Promise<void> {
    setConfirmVariant(null);
    await publish();
  }

  function onSaveClick(): void {
    if (campaign.isPublished) {
      setConfirmVariant('live-save');
    } else {
      void doSave();
    }
  }

  async function confirmLiveSave(): Promise<void> {
    setConfirmVariant(null);
    await doSave();
  }

  /*
   * Navigation is deferred to the effect below rather than performed here. Closing the modal and
   * calling history.push in the same synchronous tick races Mantine's modal teardown (focus-trap /
   * react-remove-scroll) against the route change and leaves the destination rendered but unpainted
   * (blank page). Closing the modal now and navigating from a passive effect lets the modal fully
   * unmount and clean up first. pendingLocationRef stays set so the effect knows to navigate; only
   * Cancel clears it.
   */
  function discardAndContinue(): void {
    campaign.discardDirty();
    // Whatever the warning counted as unsaved has to be reverted too, or discarding would leave
    // the edits live in the store for the next visit to the page.
    dirtyChildren.forEach((model) => model.discardDirty());
    setConfirmVariant(null);
  }

  async function continueAfterSave(savedCampaign: Campaign, stateChanged?: boolean): Promise<void> {
    await postSave(savedCampaign, stateChanged);
    // Don't navigate away after a failed save — stay on the editor so the user can see the error toast.
    if (savedCampaign.isErrored) {
      pendingLocationRef.current = null;
    }
    setConfirmVariant(null);
  }

  function closeConfirmModal(): void {
    // Cancel: stay on the page, keep changes. Clear the pending location so the deferred-navigation
    // effect does not fire.
    pendingLocationRef.current = null;
    setConfirmVariant(null);
  }

  // Block in-app navigation while there are unsaved changes; show the unsaved-navigation modal.
  useEffect(() => {
    const unblock = history.block((location) => {
      if (bypassRef.current) {
        bypassRef.current = false;
        return undefined;
      }
      const isHashOnlyChange =
        location.pathname === history.location.pathname &&
        location.search === history.location.search &&
        location.hash !== history.location.hash;
      if (isHashOnlyChange) {
        return undefined;
      }
      if (isDirty) {
        pendingLocationRef.current = location as Location;
        setConfirmVariant('unsaved-navigation');
        return false;
      }
      return undefined;
    });
    return unblock;
  }, [history, isDirty]);

  // Warn on tab close / refresh while dirty.
  useEffect(() => {
    if (isDirty) {
      setRedirectWarning();
    } else {
      unsetRedirectWarning();
    }
    return (): void => unsetRedirectWarning();
  }, [isDirty]);

  /*
   * Deferred navigation. When the unsaved-navigation modal closes (confirmVariant → null) with a
   * pending location still set — i.e. the user chose Save or Discard, not Cancel — navigate. Doing
   * this from a passive effect (rather than synchronously when the modal closes) guarantees the
   * modal has unmounted and Mantine has restored its focus-trap / scroll-lock before we route away.
   * Navigating in the same tick as the close leaves the destination rendered but unpainted.
   */
  useEffect(() => {
    if (confirmVariant !== null) {
      return;
    }
    const location = pendingLocationRef.current;
    if (!location) {
      return;
    }
    pendingLocationRef.current = null;
    bypassRef.current = true;
    history.push(location.pathname + location.search + location.hash);
  }, [confirmVariant, history]);

  return {
    confirmVariant,
    closeConfirmModal,
    onPublishClick,
    onSaveClick,
    confirmLiveSave,
    confirmRepublish,
    discardAndContinue,
    continueAfterSave,
    isAudienceModalOpen,
    closeAudienceModal,
    confirmAudience,
    isPendingImportsModalOpen,
    closePendingImportsModal,
    publishAnyway,
    isDirty,
    isSaving,
  };
}
